import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Divider, Grid, IconButton, Paper, Typography,
} from '@material-ui/core';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import type { GridColDef } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import type { ITag } from '../store/StoreInterfaces';
import globalUseStyles from '../../../globalClasses';
import StoreActionMenu from './StoreActionMenu';
import type { ICompany } from '../../massReceive/MassReceiveInterfaces';
import CompanyFilter from '../../policyConfigurator/shared/RuleFilters/CompanyFilter';
import { emptyCompany } from '../../policyConfigurator/shared/Utilities/PolicyConfiguratorHelpers';
import type ICompanyContactDetails from '../company/CompanyInterfaces';

interface IStoreInfo{
  id: string;
  name: string;
  city: string;
  state: string;
  zipCode: string;
  tags: Array<ITag>;
}

const AllStores: React.FC = () => {
  const navigate = useNavigate();
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const companyIdParam = searchParams.get('companyId');

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [storeId, setStoreId] = React.useState<string | null>(null);
  const [company, setCompany] = React.useState<ICompany>(companyIdParam ? { companyId: companyIdParam, companyName: '' } : emptyCompany);

  const handleUpdateCompany = (companyParam: ICompany) => {
    setCompany(companyParam);
  };

  const StoreFetcher = (url: string, companyId: string) => axios.get<Array<IStoreInfo>>(url, {
    params: {
      companyId,
    },
  })
    .then((response) => {
      if (companyId) {
        console.log(`${companyId} is companyId`);

        axios.get<ICompanyContactDetails>('/company/getCompany',
          {
            params: {
              companyId,
            },
          })
          .then((result) => {
            setCompany({ ...company, companyId, companyName: result.data.name });
          }).catch((error: AxiosError) => {
            if (error?.message) {
              enqueueSnackbar(`Unable to find company by id: ${companyId}.`, { persist: false, variant: 'error' });
            }
          });
      }
      return response.data;
    })
    .catch((e: AxiosError) => {
      if (e.response?.status === 404) {
        enqueueSnackbar(`There was the following error when fetching the stores: ${JSON.stringify(e.response.data)}`, { persist: false, variant: 'error' });
      } else {
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const { data } = useSWR(
    company.companyId !== emptyCompany().companyId
      ? ['/store/getStoresForCompany', company.companyId]
      : null,
    (url) => StoreFetcher(url, company?.companyId),
  );

  const handleAddStoreBtnClicked = () => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    navigate(`/store/?companyId=${company.companyId}`);
  };

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      console.log(`id: ${id}`);
      setStoreId(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setStoreId(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (StoreIdParameter: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, StoreIdParameter)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const manualAddColumns: GridColDef[] = [
    { field: 'storeName', headerName: 'Store Name', flex: 1 },
    { field: 'city', headerName: 'City', flex: 1 },
    { field: 'state', headerName: 'State', flex: 1 },
    { field: 'zipCode', headerName: 'ZipCode', flex: 0.5 },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1.5,
      renderCell: ({ value }) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const tagArray = (value as ITag[]).map((tag) => `${tag.key}:${tag.value}`);
        return tagArray.join(', ');
      },
    },
    {
      field: 'id',
      type: 'string',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  return (
    <Grid>
      <Grid container>
        <Grid container>
          <Grid item xs={6}>
            <Box pb={3} pt={3}>
              <Typography variant="h4" className={globalStyles.labelText}>{'Stores'}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Grid container direction="row-reverse">
              <Box pb={3} pt={3}>
                <Button color="primary" variant="contained" onClick={handleAddStoreBtnClicked}>{'ADD STORE'}</Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box py={3}>
            <Paper>
              <Grid item xs={12}>
                <Box pl={2} py={2}>
                  <Typography variant="h4">{'Select Company'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Box pl={2} py={2}>
                <CompanyFilter
                  companyFormValue={company}
                  setCompanyFormValue={handleUpdateCompany}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      { company.companyName !== '' && (
        <>
          <Grid item xs={12}>
            <Box pb={3}>
              <Paper elevation={4}>
                <Box p={3}>
                  <Typography variant="h6">{'Stores'}</Typography>
                </Box>
                {data
          && (
            <>
              <DataGrid
                rows={data}
                columns={manualAddColumns}
                autoHeight
                checkboxSelection
                className={globalStyles.transparentDataGrid}
              />
              {
              storeId && anchorEl && (
                <StoreActionMenu
                  storeId={storeId}
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                />
              )
            }
            </>
          )}
              </Paper>
            </Box>
          </Grid>
          <Grid container xs={12} direction="row-reverse">
            <Grid item>
              <Box pt={3}>
                <Button disabled color="primary" variant="text" onClick={() => { Error('Not implemented'); }}>{'BULK IMPORT STORES'}</Button>
                <Button color="primary" variant="contained" onClick={handleAddStoreBtnClicked}>{'ADD STORE'}</Button>
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default AllStores;
